import React from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider } from '@clerk/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { axiosInstance } from '@edison/api-client-react';
import App from './App.js';
import { ErrorBoundary } from './components/error-boundary.js';
import './index.css';
import './lib/diagnostics.js'; // initialise console error capture + ring buffer

// Install the simulation interceptor before any API calls are made.
// This must happen at module load time, before React renders.
const SIM_MODE =
  !import.meta.env['VITE_API_BASE_URL'] ||
  import.meta.env['VITE_SIM_MODE'] === 'true';

if (SIM_MODE) {
  // Dynamic import keeps sim.ts out of the production bundle path.
  // The promise resolves before any user interaction requires API calls.
  import('./lib/sim.js').then(({ installSimInterceptor }) => {
    installSimInterceptor(axiosInstance);
  });
}

const clerkPublishableKey = import.meta.env['VITE_CLERK_PUBLISHABLE_KEY'];
if (!clerkPublishableKey) {
  throw new Error('VITE_CLERK_PUBLISHABLE_KEY is required');
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      routerPush={(to: string) => {
        window.history.pushState(null, '', to);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }}
      routerReplace={(to: string) => {
        window.history.replaceState(null, '', to);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary variant="app">
          <App />
        </ErrorBoundary>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ClerkProvider>
  </React.StrictMode>
);
